import { useCallback, type ReactNode } from 'react';
import { AppDetailLayout } from '@/shared/app-detail-layout';
import type { DetailSection, DetailStatus } from '@/shared/app-detail-layout/types';
import { useAsyncResource } from '@/shared/hooks/use-async-resource';
import { InventoryApi } from '@/modules/inventory/services/api';
import type { StockBalance, StockMovement } from '@/api/schemas/inventory';

/**
 * One stocked item: its balance and its movement history.
 *
 * M18 keys both reads by the item id — the offer the stock is for — so the
 * detail page asks for the balance list filtered to the item and the movement
 * list filtered to the item.
 */

function formatInstant(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
}

type ItemDetail = {
  balance: StockBalance | null;
  movements: StockMovement[];
};

async function loadItem(itemId: string): Promise<ItemDetail> {
  const [balances, movements] = await Promise.all([
    InventoryApi.listBalances(),
    InventoryApi.listMovements(itemId),
  ]);
  return { balance: balances.find((row) => row.itemId === itemId) ?? null, movements };
}

export function DetailPage({ itemId }: { itemId: string }): ReactNode {
  const load = useCallback(() => loadItem(itemId), [itemId]);
  const { data, error, isLoading } = useAsyncResource(load);

  const balance = data?.balance ?? null;
  const movements = data?.movements ?? [];

  const status: DetailStatus | undefined = balance
    ? { label: `${balance.available} available`, tone: 'neutral' }
    : undefined;

  const sections: ReadonlyArray<DetailSection> = [
    { id: 'balance', label: 'Balance', content: <BalancePanel balance={balance} /> },
    { id: 'movements', label: 'Movements', content: <MovementList movements={movements} /> },
  ];

  return (
    <AppDetailLayout
      title={itemId}
      subtitle="Stock movements for this item"
      backTo="/inventory/stock"
      status={status}
      sections={sections}
      isLoading={isLoading}
      error={error}
      notFound={!isLoading && error === null && balance === null && movements.length === 0}
    />
  );
}

function BalancePanel({ balance }: { balance: StockBalance | null }): ReactNode {
  if (!balance) {
    return <p className="text-sm text-content-muted">No balance is recorded for this item.</p>;
  }

  const rows: ReadonlyArray<readonly [string, string]> = [
    ['On hand', balance.onHand],
    ['Reserved', balance.reserved],
    ['Available', balance.available],
    ['Unit', balance.unitOfMeasure],
  ];

  return (
    <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
      {rows.map(([label, value]) => (
        <div key={label}>
          <dt className="text-xs tracking-wide text-content-muted uppercase">{label}</dt>
          <dd className="text-sm text-content">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function MovementList({ movements }: { movements: StockMovement[] }): ReactNode {
  if (movements.length === 0) {
    return <p className="text-sm text-content-muted">No movement has been recorded for this item.</p>;
  }

  return (
    <ul className="flex flex-col gap-2">
      {movements.map((movement) => (
        <li
          key={movement.id}
          className="flex items-center justify-between gap-4 border-b border-content-muted/20 py-2 last:border-b-0"
        >
          <div className="flex flex-col">
            <span className="text-sm text-content">{movement.movementType}</span>
            <span className="text-xs text-content-muted">
              {movement.reason ?? 'No reason recorded'} · {formatInstant(movement.createdAt)}
            </span>
          </div>
          <span className="text-sm font-medium text-content">
            {movement.quantity} {movement.unitOfMeasure}
          </span>
        </li>
      ))}
    </ul>
  );
}
